// src/components/BillingLockScreen.tsx
// Full page overlay when billing.status === 'locked'
// Wrap your main app routes with this — it intercepts rendering when locked

import React, { useState } from 'react';
import {
  Box, Typography, Button, Stack, TextField,
  CircularProgress, Alert, Divider, Chip,
} from '@mui/material';
import LockOutlinedIcon   from '@mui/icons-material/LockOutlined';
import PaymentsIcon       from '@mui/icons-material/Payments';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import LogoutIcon         from '@mui/icons-material/Logout';
import { useAuth }        from '../contexts/AuthContext';
import api                from '../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────
interface BankDetails {
  bankName: string;
  accountNumber: string;
  accountName: string;
  sortCode?: string;
}

interface OutstandingInvoice {
  _id: string;
  invoiceNumber: string;
  total: number;
  currency: string;
  dueAt: string;
  amountRemaining: number;
  paymentSubmittedReference?: string;
}

// ── Lock screen ───────────────────────────────────────────────────────────────
const BillingLockScreen: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { billingStatus, user, company, logout, refreshBillingStatus } = useAuth();

  const [step, setStep]             = useState<'locked' | 'paying' | 'submitted'>('locked');
  const [reference, setReference]   = useState('');
  const [notes, setNotes]           = useState('');
  const [invoiceId, setInvoiceId]   = useState<string | null>(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [bankDetails, setBankDetails] = useState<BankDetails | null>(null);
  const [invoices, setInvoices]     = useState<OutstandingInvoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const isLocked = billingStatus?.status === 'locked';
  const isAdmin  = user?.role === 'company_admin';

  // Fetch outstanding invoices and bank details when user clicks Pay
  const handleStartPayment = async () => {
    setLoadingInvoices(true);
    setError('');
    try {
      const [invRes, currentRes] = await Promise.all([
        api.get('/company-invoices?status=overdue'),
        api.get('/company-invoices/current'),
      ]);
      const invList = invRes.data.data || [];
      setInvoices(invList);
      setBankDetails(currentRes.data.data?.bankDetails || null);

      // Pre-select first overdue invoice
      if (invList.length > 0) setInvoiceId(invList[0]._id);

      setStep('paying');
    } catch (e: any) {
      setError('Failed to load invoice details. Please try again.');
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleSubmitPayment = async () => {
    if (!reference.trim()) { setError('Payment reference is required'); return; }
    if (!invoiceId)         { setError('No invoice selected'); return; }

    setLoading(true);
    setError('');
    try {
      await api.post(`/company-invoices/${invoiceId}/submit-payment`, {
        paymentReference: reference.trim(),
        paymentNotes:     notes.trim() || undefined,
      });
      setStep('submitted');
      // Refresh billing status in case admin confirmed instantly
      setTimeout(refreshBillingStatus, 3000);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to submit payment reference');
    } finally {
      setLoading(false);
    }
  };

  // Not locked — render children normally
  if (!isLocked) return <>{children}</>;

  const fmt = (n: number, currency = 'NGN') => {
    try { return new Intl.NumberFormat('en-NG', { style: 'currency', currency }).format(n); }
    catch { return `${currency} ${n.toLocaleString()}`; }
  };

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0a0a0f 0%, #0f0a1a 50%, #0a0f1a 100%)',
      overflow: 'auto',
      py: 4,
    }}>
      {/* Background grid */}
      <Box sx={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
      }} />

      {/* Glow */}
      <Box sx={{
        position: 'fixed', top: '20%', left: '50%', transform: 'translateX(-50%)',
        width: 600, height: 300,
        background: 'radial-gradient(ellipse, rgba(239,68,68,0.08) 0%, transparent 70%)',
        pointerEvents: 'none', zIndex: 0,
      }} />

      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 480, mx: 'auto', px: 2 }}>

        {/* ── STEP 1: Locked ── */}
        {step === 'locked' && (
          <Box>
            {/* Lock icon */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{
                width: 80, height: 80, borderRadius: 3,
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                display: 'grid', placeItems: 'center',
                mx: 'auto', mb: 3,
                boxShadow: '0 0 40px rgba(239,68,68,0.15)',
              }}>
                <LockOutlinedIcon sx={{ fontSize: 36, color: '#ef4444' }} />
              </Box>
              <Typography sx={{
                fontFamily: '"DM Serif Display", "Playfair Display", Georgia, serif',
                fontSize: '1.75rem',
                fontWeight: 400,
                color: '#fff',
                lineHeight: 1.2,
                mb: 1,
              }}>
                Account Locked
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.45)', maxWidth: 340, mx: 'auto', lineHeight: 1.6 }}>
                Access to <strong style={{ color: 'rgba(255,255,255,0.7)' }}>{company?.name || 'your account'}</strong> has been suspended due to an outstanding invoice.
              </Typography>
            </Box>

            {/* Company badge */}
            <Box sx={{
              p: 2, borderRadius: 2,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              mb: 3,
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack>
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.25 }}>Logged in as</Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#fff', fontWeight: 700 }}>{user?.firstName} {user?.lastName}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>{user?.email}</Typography>
                </Stack>
                <Chip
                  label="LOCKED"
                  size="small"
                  sx={{ background: 'rgba(239,68,68,0.15)', color: '#f87171', fontWeight: 800, fontSize: '0.65rem', letterSpacing: '0.1em', border: '1px solid rgba(239,68,68,0.3)' }}
                />
              </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

            {/* Actions */}
            <Stack spacing={1.5}>
              {isAdmin ? (
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={loadingInvoices ? <CircularProgress size={16} color="inherit" /> : <PaymentsIcon />}
                  onClick={handleStartPayment}
                  disabled={loadingInvoices}
                  sx={{
                    borderRadius: 2, textTransform: 'none', fontWeight: 800,
                    fontSize: '0.9rem', py: 1.5,
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    boxShadow: '0 4px 20px rgba(239,68,68,0.3)',
                    '&:hover': { background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' },
                  }}
                >
                  Submit Payment Reference
                </Button>
              ) : (
                <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)', textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.8rem', color: '#fde68a', fontWeight: 600 }}>
                    Please contact your company administrator to resolve the outstanding payment.
                  </Typography>
                </Box>
              )}

              <Button
                fullWidth
                variant="text"
                startIcon={<LogoutIcon />}
                onClick={logout}
                sx={{
                  borderRadius: 2, textTransform: 'none', fontWeight: 600,
                  color: 'rgba(255,255,255,0.35)',
                  '&:hover': { color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.04)' },
                }}
              >
                Sign out
              </Button>
            </Stack>
          </Box>
        )}

        {/* ── STEP 2: Payment form ── */}
        {step === 'paying' && (
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
              <Box
                component="button"
                onClick={() => setStep('locked')}
                sx={{
                  background: 'none', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 1.5, px: 1, py: 0.5, cursor: 'pointer',
                  color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', fontWeight: 700,
                  '&:hover': { borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)' },
                }}
              >
                ← Back
              </Box>
              <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '1rem' }}>
                Submit Payment Reference
              </Typography>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

            {/* Outstanding invoices */}
            {invoices.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1 }}>
                  Outstanding Invoice{invoices.length > 1 ? 's' : ''}
                </Typography>
                {invoices.map(inv => (
                  <Box
                    key={inv._id}
                    onClick={() => setInvoiceId(inv._id)}
                    sx={{
                      p: 1.5, borderRadius: 2, cursor: 'pointer', mb: 1,
                      background: invoiceId === inv._id ? 'rgba(239,68,68,0.1)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${invoiceId === inv._id ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.08)'}`,
                      transition: 'all 0.15s',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography sx={{ fontSize: '0.83rem', fontWeight: 800, color: '#fff' }}>
                          {inv.invoiceNumber}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}>
                          Due {new Date(inv.dueAt).toLocaleDateString()}
                          {inv.paymentSubmittedReference && (
                            <span style={{ color: '#38bdf8', marginLeft: 8 }}>· Ref submitted</span>
                          )}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 900, color: '#f87171', fontSize: '0.9rem' }}>
                        {fmt(inv.amountRemaining ?? inv.total, inv.currency)}
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Box>
            )}

            {/* Bank details */}
            {bankDetails?.accountNumber && (
              <Box sx={{
                p: 2, borderRadius: 2, mb: 3,
                background: 'rgba(167,139,250,0.06)',
                border: '1px solid rgba(167,139,250,0.2)',
              }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <AccountBalanceIcon sx={{ fontSize: 14, color: '#a78bfa' }} />
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    Pay to this account
                  </Typography>
                </Stack>
                {[
                  { label: 'Bank',           value: bankDetails.bankName },
                  { label: 'Account Name',   value: bankDetails.accountName },
                  { label: 'Account Number', value: bankDetails.accountNumber },
                  ...(bankDetails.sortCode ? [{ label: 'Sort Code', value: bankDetails.sortCode }] : []),
                ].map(row => (
                  <Stack key={row.label} direction="row" justifyContent="space-between" sx={{ py: 0.4 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)' }}>{row.label}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>{row.value}</Typography>
                  </Stack>
                ))}
              </Box>
            )}

            {/* Reference input */}
            <Stack spacing={2} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Bank Transfer Reference *"
                value={reference}
                onChange={e => setReference(e.target.value)}
                size="small"
                placeholder="e.g. TRF20240201001"
                helperText="Enter the reference number from your bank transfer receipt"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2, background: 'rgba(255,255,255,0.04)',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                    '&:hover fieldset': { borderColor: '#ef4444' },
                    '&.Mui-focused fieldset': { borderColor: '#ef4444' },
                    '& input': { color: '#fff' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#ef4444' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.3)' },
                }}
              />
              <TextField
                fullWidth
                label="Additional Notes (optional)"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                size="small"
                multiline rows={2}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2, background: 'rgba(255,255,255,0.04)',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                    '&:hover fieldset': { borderColor: '#ef4444' },
                    '&.Mui-focused fieldset': { borderColor: '#ef4444' },
                    '& textarea': { color: '#fff' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#ef4444' },
                }}
              />
            </Stack>

            <Stack spacing={1.5}>
              <Button
                fullWidth variant="contained" size="large"
                onClick={handleSubmitPayment}
                disabled={loading || !reference.trim()}
                sx={{
                  borderRadius: 2, textTransform: 'none', fontWeight: 800,
                  fontSize: '0.875rem', py: 1.4,
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  boxShadow: '0 4px 20px rgba(239,68,68,0.25)',
                  '&:hover': { background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' },
                  '&:disabled': { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.2)' },
                }}
              >
                {loading ? <CircularProgress size={18} color="inherit" /> : 'Submit Payment Reference'}
              </Button>
              <Button
                fullWidth variant="text" onClick={logout}
                sx={{
                  borderRadius: 2, textTransform: 'none', fontWeight: 600,
                  color: 'rgba(255,255,255,0.3)',
                  '&:hover': { color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.04)' },
                }}
              >
                Sign out
              </Button>
            </Stack>
          </Box>
        )}

        {/* ── STEP 3: Submitted ── */}
        {step === 'submitted' && (
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              width: 72, height: 72, borderRadius: '50%',
              background: 'rgba(52,211,153,0.1)',
              border: '1px solid rgba(52,211,153,0.3)',
              display: 'grid', placeItems: 'center',
              mx: 'auto', mb: 3,
              boxShadow: '0 0 40px rgba(52,211,153,0.15)',
            }}>
              <CheckCircleIcon sx={{ fontSize: 32, color: '#34d399' }} />
            </Box>

            <Typography sx={{
              fontFamily: '"DM Serif Display", "Playfair Display", Georgia, serif',
              fontSize: '1.5rem', color: '#fff', mb: 1,
            }}>
              Reference Submitted
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, mb: 3, maxWidth: 340, mx: 'auto' }}>
              Your payment reference has been sent to the administrator for confirmation.
              Once verified, your account will be unlocked automatically.
            </Typography>

            <Box sx={{
              p: 2, borderRadius: 2, mb: 3,
              background: 'rgba(52,211,153,0.06)',
              border: '1px solid rgba(52,211,153,0.2)',
            }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#6ee7b7', lineHeight: 1.6 }}>
                <strong>What happens next:</strong><br />
                The administrator will review your payment reference and confirm it.
                You'll regain full access as soon as it's confirmed — usually within a few hours.
              </Typography>
            </Box>

            <Button
              fullWidth variant="text" onClick={logout}
              sx={{
                borderRadius: 2, textTransform: 'none', fontWeight: 600,
                color: 'rgba(255,255,255,0.3)',
                '&:hover': { color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.04)' },
              }}
            >
              Sign out
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BillingLockScreen;